import { HtmlPrompt, PromptGroup, PromptPosition } from '../types/prompts.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { type AnimationCoordinator, AnimationType } from './animation/animation-types.ts'
import { CssAnimationCoordinator } from './animation/css-animation-coordinator.ts'
import { Env } from '../env.ts'
import { type PromptDisplayOutcome, PromptDisplayCoordinator, DisplayCoordinatorDelegate } from '../types/display-coordinator.ts'
import { buildSlidePromptElement } from './slide-prompt-dom.ts'
import { DebugController } from '../debug/debug-controller.ts'
import { DebugEventReason } from '../enums/debug-event-reason.ts'
import type { PromptDispatchMessage } from '../types/prompt-dispatch-message.ts'
import { PromptActionType } from '../types/prompt-action-type.ts'

/** @internal */
export class PromptView implements PromptDisplayCoordinator {
    private readonly logger: Logger
    private readonly animationCoordinator: AnimationCoordinator

    public constructor(
        public readonly prompt: HtmlPrompt,
        public readonly group: PromptGroup,
        protected readonly delegate: DisplayCoordinatorDelegate & {
            _processDispatchMessage(message: PromptDispatchMessage): Promise<void>
        },
        private readonly debugController: DebugController,
        logger?: Logger,
    ) {
        this.logger = logger ?? TaggedLogger._getInstance('PromptView')

        this.animationCoordinator = new CssAnimationCoordinator(prompt)
    }

    protected get document(): Document {
        return Env._document
    }

    protected get isMobile() {
        return Env._devicePlacement === 'mobile'
    }

    /**
     * `theme.position` is declared required on {@link PromptTheme}, but
     * {@link buildSlidePromptElement} deliberately renders a theme that omits it entirely or
     * partially (see `themeHasUsableContent`/`buildContainerClassName`) — the type is a shape
     * contract, not a runtime guarantee against a stale or malformed payload. Every access here is
     * therefore optionally chained, falling back to {@link PromptPosition.TOP} to match the
     * stylesheet's own unpositioned default.
     */
    protected get position() {
        const position = this.prompt.theme?.position
        return this.isMobile ? position?.mobile ?? PromptPosition.TOP : position?.desktop ?? PromptPosition.TOP
    }

    /** The element currently animating out, if any. See the note in {@link _display}. */
    private hidingElement: HTMLElement | null = null

    protected get promptElementId(): string {
        return `push-sdk-prompt-${this.prompt.id}`
    }

    protected get promptElement(): HTMLElement | null {
        return this.document.getElementById(this.promptElementId)
    }

    public _display(): PromptDisplayOutcome {
        // A prompt that is animating out is on its way off the page, not on it. `_hide()` tells
        // the delegate immediately but the element stays in the DOM until the animation ends, so
        // a re-show landing in that window would otherwise be told the prompt is already
        // displaying — the controller would keep the view, skip re-enabling evaluation, and then
        // the animation would remove the element, leaving prompting dead with nothing on screen.
        if (this.hidingElement) {
            this.hidingElement.remove()
            this.hidingElement = null
        }

        if (this.promptElement) {
            this.logger.warn(`Prompt ${this.prompt.id} is already displaying`)
            return 'already-displaying'
        }

        const promptElement = buildSlidePromptElement(this.prompt, {
            onAllow: () => void this._allow(),
            onDismiss: () => void this._dismiss(),
        })

        if (!promptElement) {
            this.logger.warn(`Prompt ${this.prompt.id} has no usable theme; nothing will be displayed`)
            this.debugController._debugPromptNotShown(DebugEventReason.SLIDE_PROMPT_THEME_UNUSABLE)
            return 'nothing-to-render'
        }

        // Pin the prompt buttons' layout-critical properties so host `button`
        // resets/!important rules cannot clip them. The `.push-sdk-prompt-container button`
        // selector (0,1,1) outranks a bare `button` selector (0,0,1), so these
        // !important declarations win regardless of stylesheet source order.
        // Only layout-critical properties are pinned; the prompt keeps inheriting
        // the host site's other styles for a native look.
        const buttonProtectionStyles = this.document.createElement('style')
        buttonProtectionStyles.textContent =
            '.push-sdk-prompt-container button {' +
            ' box-sizing: border-box !important;' +
            ' min-width: 0 !important;' +
            ' max-width: none !important;' +
            ' width: auto !important;' +
            ' }'
        promptElement.append(buttonProtectionStyles)

        Env._document.body.append(promptElement)

        this.animationCoordinator._animateIn({
            enabled: true,
            animations: [
                AnimationType.FADE,
                this.position === PromptPosition.TOP ? AnimationType.SLIDE_DOWN : AnimationType.SLIDE_UP,
            ],
            duration_milliseconds: 420,
        })

        this.logger.verbose(`Displaying Prompt ${this.prompt.id}`)
        this.delegate._onPromptDidShow(this.prompt)

        return 'displayed'
    }

    public _hide(): void {
        const promptElement = this.promptElement
        if (!promptElement) {
            this.logger.warn(`Prompt ${this.prompt.id} is already hidden`)
            return
        }

        this.logger.verbose(`Hiding ${this.prompt.id}`)
        this.hidingElement = promptElement
        this.delegate._onPromptDidHide(this.prompt)

        this.animationCoordinator._animateOut(
            {
                enabled: true,
                animations: [
                    AnimationType.FADE,
                    this.position === PromptPosition.TOP ? AnimationType.SLIDE_UP : AnimationType.SLIDE_DOWN,
                ],
                duration_milliseconds: 420,
            },
            () => {
                promptElement.remove()

                if (this.hidingElement === promptElement) {
                    this.hidingElement = null
                }
            },
        )
    }

    /**
     * Dispatches standard **allow** {@link PromptDispatchMessage}, mirroring
     * {@link CustomPromptView._allow}.
     */
    protected async _allow(): Promise<void> {
        await this.delegate._processDispatchMessage({
            prompt_action: {
                id: this.prompt.id,
                type: PromptActionType.ALLOW,
            },
        })
    }

    /**
     * Dispatches standard **dismiss** {@link PromptDispatchMessage}, mirroring
     * {@link CustomPromptView._dismiss}.
     */
    protected async _dismiss(): Promise<void> {
        await this.delegate._processDispatchMessage({
            prompt_action: {
                id: this.prompt.id,
                type: PromptActionType.DISMISS,
            },
        })
    }
}
